import { useState, useContext } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import { ColorModeContext } from '../contexts/ColorModeContext';
import { palette, typography, theme as themeColors } from '../theme/colors';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface FilterOption {
  field: string;
  label: string;
  type: 'select' | 'text' | 'date';
  options?: { value: string; label: string }[];
}

export interface ColumnConfig {
  field: string;
  headerName: string;
  valueFormatter?: (value: any, row: any) => string;
}

interface TableToolbarProps {
  title: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  filters?: FilterOption[];
  activeFilters?: Record<string, string>;
  onFilterChange?: (field: string, value: string) => void;
  onClearFilters?: () => void;
  data: any[];
  columns: ColumnConfig[];
  exportFileName?: string;
  hideSearch?: boolean;
}

export const TableToolbar = ({
  title,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  filters = [],
  activeFilters = {},
  onFilterChange,
  onClearFilters,
  data,
  columns,
  exportFileName = 'export',
  hideSearch = false,
}: TableToolbarProps) => {
  const { mode } = useContext(ColorModeContext);
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);

  const colors = mode === 'dark' ? {
    bg: palette.surface,
    bgElevated: palette.surfaceElevated,
    bgHover: palette.surfaceHover,
    border: palette.border,
    borderLight: palette.borderLight,
    text: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.surface,
    bgElevated: palette.light.surfaceElevated,
    bgHover: palette.light.surfaceHover,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    text: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const activeFilterCount = Object.values(activeFilters).filter(v => v !== '').length;

  const handleExportCSV = () => {
    setExportAnchorEl(null);

    const headers = columns.map(col => col.headerName);
    const rows = data.map(row =>
      columns.map(col => {
        const value = row[col.field];
        if (col.valueFormatter) {
          return col.valueFormatter(value, row);
        }
        return value ?? '';
      })
    );

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${exportFileName}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleExportPDF = () => {
    setExportAnchorEl(null);

    const doc = new jsPDF();

    // Add title
    doc.setFontSize(18);
    doc.setTextColor(99, 102, 241); // Primary color
    doc.text(title, 14, 22);

    // Add date
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 30);

    const headers = columns.map(col => col.headerName);
    const rows = data.map(row =>
      columns.map(col => {
        const value = row[col.field];
        if (col.valueFormatter) {
          return col.valueFormatter(value, row);
        }
        return value ?? '';
      })
    );

    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 38,
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [99, 102, 241],
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      margin: { top: 38 },
    });

    doc.save(`${exportFileName}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: colors.bg,
      borderRadius: themeColors.radius.lg,
      fontSize: typography.fontSize.sm,
      '& fieldset': {
        borderColor: colors.border,
      },
      '&:hover fieldset': {
        borderColor: colors.borderLight,
      },
      '&.Mui-focused fieldset': {
        borderColor: palette.primary,
      },
    },
    '& .MuiInputBase-input': {
      color: colors.text,
      '&::placeholder': {
        color: colors.textMuted,
        opacity: 1,
      },
    },
  };

  const buttonStyles = {
    borderRadius: themeColors.radius.lg,
    borderColor: colors.border,
    color: colors.textSecondary,
    textTransform: 'none' as const,
    fontWeight: typography.fontWeight.medium,
    px: 2,
    '&:hover': {
      borderColor: colors.borderLight,
      backgroundColor: colors.bgHover,
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        mb: 3,
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
      }}
    >
      {/* Search Field */}
      {!hideSearch && (
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{
            ...inputStyles,
            minWidth: { xs: '100%', sm: 300 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: searchValue && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onSearchChange('')}
                  sx={{ color: colors.textMuted }}
                >
                  <ClearIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        {/* Active Filters Display */}
        {activeFilterCount > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {Object.entries(activeFilters)
              .filter(([, value]) => value !== '')
              .map(([field, value]) => {
                const filterConfig = filters.find(f => f.field === field);
                const displayValue = filterConfig?.options?.find(o => o.value === value)?.label || value;
                return (
                  <Chip
                    key={field}
                    label={`${filterConfig?.label}: ${displayValue}`}
                    size="small"
                    onDelete={() => onFilterChange?.(field, '')}
                    sx={{
                      backgroundColor: palette.primaryMuted,
                      color: palette.primary,
                      borderRadius: themeColors.radius.md,
                      '& .MuiChip-deleteIcon': {
                        color: palette.primary,
                        '&:hover': {
                          color: palette.primaryDark,
                        },
                      },
                    }}
                  />
                );
              })}
            <IconButton
              size="small"
              onClick={onClearFilters}
              sx={{ color: colors.textMuted }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}

        {/* Filter Button */}
        {filters.length > 0 && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListOutlinedIcon />}
              onClick={(e) => setFilterAnchorEl(e.currentTarget)}
              sx={{
                ...buttonStyles,
                ...(activeFilterCount > 0 && {
                  borderColor: palette.primary,
                  color: palette.primary,
                }),
              }}
            >
              Filters
              {activeFilterCount > 0 && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    backgroundColor: palette.primary,
                    color: '#fff',
                    borderRadius: '50%',
                    width: 18,
                    height: 18,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                  }}
                >
                  {activeFilterCount}
                </Box>
              )}
            </Button>

            <Popover
              open={Boolean(filterAnchorEl)}
              anchorEl={filterAnchorEl}
              onClose={() => setFilterAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  p: 2,
                  minWidth: 280,
                  borderRadius: themeColors.radius.lg,
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.bgElevated,
                },
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: typography.fontWeight.semibold,
                  color: colors.text,
                  mb: 2,
                }}
              >
                Filter by
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filters.map((filter) => (
                  <FormControl key={filter.field} size="small" fullWidth>
                    <InputLabel sx={{ color: colors.textSecondary }}>{filter.label}</InputLabel>
                    <Select
                      value={activeFilters[filter.field] || ''}
                      onChange={(e) => onFilterChange?.(filter.field, e.target.value)}
                      label={filter.label}
                      sx={inputStyles}
                    >
                      <MenuItem value="">All</MenuItem>
                      {filter.options?.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
              </Box>
              {activeFilterCount > 0 && (
                <>
                  <Divider sx={{ my: 2, borderColor: colors.border }} />
                  <Button
                    size="small"
                    onClick={() => {
                      onClearFilters?.();
                      setFilterAnchorEl(null);
                    }}
                    sx={{ color: palette.error }}
                  >
                    Clear all filters
                  </Button>
                </>
              )}
            </Popover>
          </>
        )}

        {/* Export Button */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={(e) => setExportAnchorEl(e.currentTarget)}
          sx={buttonStyles}
        >
          Export
        </Button>

        <Menu
          anchorEl={exportAnchorEl}
          open={Boolean(exportAnchorEl)}
          onClose={() => setExportAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 160,
              borderRadius: themeColors.radius.lg,
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.bgElevated,
            },
          }}
        >
          <MenuItem
            onClick={handleExportCSV}
            sx={{
              color: colors.text,
              '&:hover': { backgroundColor: colors.bgHover },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: themeColors.radius.md,
                  backgroundColor: palette.successMuted,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: palette.success,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                CSV
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: typography.fontWeight.medium }}>
                  Export CSV
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  Spreadsheet format
                </Typography>
              </Box>
            </Box>
          </MenuItem>
          <MenuItem
            onClick={handleExportPDF}
            sx={{
              color: colors.text,
              '&:hover': { backgroundColor: colors.bgHover },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: themeColors.radius.md,
                  backgroundColor: palette.errorMuted,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: palette.error,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                PDF
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: typography.fontWeight.medium }}>
                  Export PDF
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  Document format
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};
